import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";

type Channel = {
  id: number;
  name: string;
  channel_type: string;
  commission_y1: number;
  commission_y2: number;
  commission_y3: number;
};

const CHANNEL_TYPES = [
  { value: "direct", label: "Direct" },
  { value: "master_reseller", label: "Master Reseller" },
  { value: "regular_reseller", label: "Regular Reseller" },
];

export const ChannelsConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ["channels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("channels")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as Channel[];
    },
  });

  useEffect(() => {
    if (data) setChannels(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const ch of channels) {
        const payload = {
          name: ch.name,
          channel_type: ch.channel_type,
          commission_y1: ch.commission_y1,
          commission_y2: ch.commission_y2,
          commission_y3: ch.commission_y3,
        };
        if (ch.id > 0) {
          const { error } = await supabase
            .from("channels")
            .update(payload)
            .eq("id", ch.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("channels")
            .insert(payload);
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      notify("Channels saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["channels"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleAdd = () => {
    setChannels((prev) => [
      ...prev,
      {
        id: -Date.now(),
        name: "",
        channel_type: "direct",
        commission_y1: 0,
        commission_y2: 0,
        commission_y3: 0,
      },
    ]);
    setDirty(true);
  };

  const handleDelete = async (index: number) => {
    const ch = channels[index];
    if (ch.id > 0) {
      const { error } = await supabase
        .from("channels")
        .delete()
        .eq("id", ch.id);
      if (error) {
        notify("Cannot delete — channel may have linked companies", {
          type: "error",
        });
        return;
      }
    }
    setChannels((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    queryClient.invalidateQueries({ queryKey: ["channels"] });
  };

  const handleChange = (
    index: number,
    field: keyof Channel,
    value: string,
  ) => {
    setChannels((prev) =>
      prev.map((ch, i) => {
        if (i !== index) return ch;
        if (
          field === "commission_y1" ||
          field === "commission_y2" ||
          field === "commission_y3"
        ) {
          return { ...ch, [field]: Number(value) || 0 };
        }
        return { ...ch, [field]: value };
      }),
    );
    setDirty(true);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Manage sales channels. Each channel has a type and 3-year
        commission rates.
      </p>

      <div className="space-y-3">
        {channels.map((ch, i) => (
          <div
            key={ch.id}
            className="border rounded-md p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={ch.name}
                onChange={(e) => handleChange(i, "name", e.target.value)}
                placeholder="Channel name"
                className="flex-1 px-3 py-1.5 text-sm border rounded-md"
              />
              <select
                value={ch.channel_type}
                onChange={(e) =>
                  handleChange(i, "channel_type", e.target.value)
                }
                className="h-8 rounded-md border bg-background px-2 text-sm"
              >
                {CHANNEL_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(i)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              {(["commission_y1", "commission_y2", "commission_y3"] as const).map(
                (field, yi) => (
                  <div key={field} className="flex-1">
                    <label className="text-xs text-muted-foreground">
                      Y{yi + 1} %
                    </label>
                    <input
                      type="number"
                      value={ch[field]}
                      onChange={(e) =>
                        handleChange(i, field, e.target.value)
                      }
                      className="w-full h-8 rounded-md border bg-background px-2 text-sm"
                      min={0}
                      max={100}
                      step={0.1}
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Channel
        </Button>
        {dirty && (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
